export type PlatformKey = "googleAds" | "linkedIn" | "reddit";

// Vendor-config key holding a platform's account id. A falsy or absent value
// disables the platform.
export type AccountIdKey =
  "ID_GOOGLE_ADS" | "ID_LINKEDIN_INSIGHT" | "ID_REDDIT_PIXEL";

/**
 * One ad platform. Adapters only translate a conversion token into a call on
 * the platform's tag; reading the config, checking the tag loaded and isolating
 * errors are handled by the dispatcher and `withTag`.
 */
export type AdPlatform = {
  /** Used in error reports only. */
  readonly name: string;
  /** Key under `AD_CONVERSIONS` holding this platform's conversion tokens. */
  readonly key: PlatformKey;
  readonly accountIdKey: AccountIdKey;
  /** Sends one conversion. A no-op when the tag has not loaded. */
  readonly send: (token: string, accountId: string) => void;
};

type Tag = (...args: never[]) => unknown;

/**
 * Makes a tag call a no-op when the snippet has not loaded. Snippets are
 * injected from marketing.js only on advertising consent, so an undefined global
 * means no consent. Defining the guard here keeps it out of every adapter.
 */
export const withTag =
  <T extends Tag>(
    getTag: () => T | undefined,
    send: (tag: T, token: string, accountId: string) => void
  ) =>
  (token: string, accountId: string) => {
    const tag = getTag();
    if (typeof tag !== "function") {
      return;
    }

    send(tag, token, accountId);
  };
